import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { debounceAsync } from '../../../../utils/misc/debounce';
import useFormInput from '../../../../utils/hooks/useFormInput';
import {
	checkOrganizationExistence,
	checkOrganizationOutstanding,
} from './_api';
import { raiseErrorBanner } from '../../../../components/MessageDispatch/messageDispatch';
import { raiseWarningBanner } from '../../../../components/MessageDispatch/messageDispatch';
import TextBox from '../../../../components/Form/TextBox/TextBox';
import { FORM_KEYS, VALIDATION_PATTERNS } from './_constants';

/**
 * Formik-compatible functional component for consuming, validating new third-party organization names.
 *
 * @param {Object} props JSX attributes passed to component.
 * @returns {ReactElement} rendered component.
 */
const NewThirdPartyOrganization = ({ formik, inputProps, label, ...props }) => {
	const dispatch = useDispatch();
	const { meta: { error, touched } = {} } = useFormInput(
		FORM_KEYS.ORGANIZATION_NAME
	);
	const [sCustomError, setCustomError] = useState('');

	/**
	 * Lifecycle hook on formik prop change. Updates control validation message when field
	 * is touched and noted by formik as invalid.
	 *
	 * @function
	 * @returns void. No clean-up performed.
	 */
	useEffect(() => {
		setCustomError(touched && error ? error : '');
	}, [touched, error]);

	/**
	 * Determines if @param value is valid third-party organization name based on existence.
	 *
	 * @function
	 * @param {string} value non-null organization name to validate.
	 * @returns {Promise<string>} resolved promise providing error message if name invalid, otherwise undefined.
	 */
	const validate = async value => {
		try {
			let errorMessage;
			const organizationExists = await checkOrganizationExistence(
				value,
				dispatch
			);
			let organizationPending;
			if (!organizationExists) {
				organizationPending = await checkOrganizationOutstanding(value);
			}
			errorMessage = organizationExists
				? 'Organization already exists.'
				: organizationPending
				? 'A request already exists.'
				: errorMessage;
			setCustomError(errorMessage ? errorMessage : '');
			return errorMessage;
		} catch (error) {
			if (error.message.includes('Success')) {
				raiseWarningBanner(
					error.cause ? error.cause.message : error.message,
					dispatch
				);
			} else {
				raiseErrorBanner(error.cause ? error.cause : error, dispatch);
			}
		}
	};

	/**
	 * Memoized callback debouncing `validate` function.
	 *
	 * @type {Function}
	 * @returns {Promise<string>} nullable validation message on error;
	 */
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedValidate = useCallback(
		debounceAsync(value => validate(value)),
		[]
	);

	return (
		<TextBox
			inputProps={{
				customError: sCustomError,
				pattern: VALIDATION_PATTERNS[FORM_KEYS.ORGANIZATION_NAME],
				...inputProps,
			}}
			label={label ? label : 'Third-Party Organization Name'}
			name={FORM_KEYS.ORGANIZATION_NAME}
			validate={value => debouncedValidate(value)}
			{...props}
		/>
	);
};

export { NewThirdPartyOrganization };
